import { rd, RemoteData } from '@passionware/monads';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Check,
  ChevronDown,
  Globe2,
  LogOut,
  Plus,
  Settings2,
  User
} from 'lucide-react';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import { Button } from 'v5/design-system/Button';
import { Skeleton } from 'v5/design-system/Skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { EnterpriseSidebarEmptyState } from 'v5/features/core/enterprise/EnterpriseSidebarEmptyState';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { cn } from 'v5/platform/dom/cn';
import { useTranslation } from 'react-i18next';

export type EnterpriseWorkspaceSwitcherProps = {
  workspacesRd: RemoteData<EnterpriseWorkspace[]>;
  activeWorkspaceId: string;
  activeWorkspaceName: string;
  activeWorkspaceIconUrl: string;
  organizationName: string;
  organizationIconUrl?: string;
  accountDisplayName?: string;
  accountProfileImageUrl?: string;
  getWorkspacePath: (workspaceSlug: string) => string;
  workspaceSettingsTo: string;
  organizationSettingsTo: string;
  accountDetailsTo: string;
  onLogout: () => void;
  className?: string;
  canManage?: boolean;
  onCreateWorkspace?: () => void;
  enterpriseManagementTo?: string;
};

function SwitcherSectionLabel({ children }: { children: ReactNode }) {
  return <div className="px-2 pt-2 pb-1 text-xs text-fg-muted">{children}</div>;
}

function AccountAvatar({
  name,
  imageUrl
}: {
  name: string;
  imageUrl?: string;
}) {
  const initial = name.charAt(0)?.toUpperCase() || '?';

  return (
    <div className="size-6 shrink-0 overflow-hidden rounded-full bg-bg-subtle flex items-center justify-center">
      {imageUrl ? (
        <img src={imageUrl} alt="" className="size-full object-cover" />
      ) : (
        <span className="text-[10px] font-semibold text-fg-muted">
          {initial}
        </span>
      )}
    </div>
  );
}

function WorkspaceSwitcherLoadingSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }, (_, index) => (
        <DropdownMenuItem key={`workspace-loading-${index}`} disabled>
          <Skeleton className="size-6 shrink-0 rounded-sm" />
          <Skeleton className="h-4 flex-1 rounded" />
        </DropdownMenuItem>
      ))}
    </>
  );
}

export function EnterpriseWorkspaceSwitcher({
  workspacesRd,
  activeWorkspaceId,
  activeWorkspaceName,
  activeWorkspaceIconUrl,
  organizationName,
  organizationIconUrl = '',
  accountDisplayName,
  accountProfileImageUrl,
  getWorkspacePath,
  workspaceSettingsTo,
  organizationSettingsTo,
  accountDetailsTo,
  onLogout,
  className,
  canManage = false,
  onCreateWorkspace,
  enterpriseManagementTo
}: EnterpriseWorkspaceSwitcherProps) {
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('w-auto self-start gap-2 max-w-full', className)}
        >
          <span className="text-xl truncate">{activeWorkspaceName}</span>
          <ChevronDown className="size-4 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="right" className="w-56 p-1">
        <SwitcherSectionLabel>Organization</SwitcherSectionLabel>
        <div className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-fg-default">
          <EnterpriseWorkspaceIcon
            name={organizationName}
            iconUrl={organizationIconUrl}
          />
          <span className="truncate">{organizationName}</span>
        </div>

        <SwitcherSectionLabel>Workspace</SwitcherSectionLabel>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <EnterpriseWorkspaceIcon
              name={activeWorkspaceName}
              iconUrl={activeWorkspaceIconUrl}
            />
            <span className="truncate">{activeWorkspaceName}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-56">
            <SwitcherSectionLabel>{t('switchWorkspace')}</SwitcherSectionLabel>
            <DropdownMenuGroup>
              {rd
                .journey(workspacesRd)
                .wait(<WorkspaceSwitcherLoadingSkeleton />)
                .catch(() => (
                  <DropdownMenuItem disabled>
                    {t('workspacesUnavailable')}
                  </DropdownMenuItem>
                ))
                .map(workspaces =>
                  workspaces.length === 0 ? (
                    <EnterpriseSidebarEmptyState
                      key="empty-workspaces"
                      messageKey="emptyWorkspaces"
                      className="px-2"
                    />
                  ) : (
                    workspaces.map(workspace =>
                      workspace.slug === activeWorkspaceId ? (
                        <DropdownMenuItem key={workspace.id} disabled>
                          <EnterpriseWorkspaceIcon
                            name={workspace.name}
                            iconUrl={workspace.iconUrl}
                          />
                          <span className="truncate">{workspace.name}</span>
                          <Check className="ml-auto" />
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem key={workspace.id} asChild>
                          <Link to={getWorkspacePath(workspace.slug)}>
                            <EnterpriseWorkspaceIcon
                              name={workspace.name}
                              iconUrl={workspace.iconUrl}
                            />
                            <span className="truncate">{workspace.name}</span>
                          </Link>
                        </DropdownMenuItem>
                      )
                    )
                  )
                )}
            </DropdownMenuGroup>
            {canManage && (onCreateWorkspace || enterpriseManagementTo) ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {canManage && onCreateWorkspace ? (
                    <DropdownMenuItem onClick={onCreateWorkspace}>
                      <Plus className="size-4" />
                      {t('newWorkspace')}
                    </DropdownMenuItem>
                  ) : null}
                  {canManage && enterpriseManagementTo ? (
                    <DropdownMenuItem asChild>
                      <Link to={enterpriseManagementTo}>
                        <Settings2 className="size-4" />
                        {t('manageEnterprise')}
                      </Link>
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuGroup>
              </>
            ) : null}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <SwitcherSectionLabel>Settings</SwitcherSectionLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to={accountDetailsTo}>
              <User className="size-4" />
              My account
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={workspaceSettingsTo}>
              <Building2 className="size-4" />
              Workspace
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={organizationSettingsTo}>
              <Globe2 className="size-4" />
              Organization
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        {accountDisplayName && (
          <>
            <SwitcherSectionLabel>Logged in as</SwitcherSectionLabel>
            <div className="flex items-center gap-2 rounded-sm px-2 py-1.5">
              <AccountAvatar
                name={accountDisplayName}
                imageUrl={accountProfileImageUrl}
              />
              <span className="flex-1 truncate text-sm text-fg-default">
                {accountDisplayName}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                layout="icon"
                className="size-7 shrink-0"
                onClick={onLogout}
                aria-label="Log out"
              >
                <LogOut className="size-4" />
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
